// ============================================================
// CLUBHUB – Kalender: Farbe der Kategorie und Marken am Termin
// ============================================================
// Die Farbe einer Kategorie kommt aus den Daten (`EventCategory.color`)
// und geht deshalb als CSS-Variable an das Element (`--kat`), nicht
// als Klasse — wie die Farbe der Personenkreise
// (`lib/person-colors.ts`). Gemischt wird sie mit den Flaechen der
// Foundation; ohne Kategorie steht die leise Textfarbe da.
//
// Die Marken sagen in Kurzform, was an einem Termin zu tun ist:
// abgesagt, verschoben, wie viele zugesagt haben, ob Helfer fehlen.
// Dieselben in Liste, Woche und Blatt.
// ============================================================
import type { CSSProperties } from "react";
import { Globe, Lock, Repeat, UsersRound } from "lucide-react";
import { TonBadge } from "@/components/mitglieder/status-badge";
import type { KategorieView, VorkommenView } from "@/lib/kalender/views";
import { cn } from "@/lib/utils";

export function kategorieStil(k: Pick<KategorieView, "color"> | null | undefined): CSSProperties {
  return { "--kat": k?.color ?? "var(--color-text-tertiary)" } as CSSProperties;
}

/** Grund einer Karte in der Farbe der Kategorie (16 % auf weiss). */
export const KAT_GRUND = "bg-[color-mix(in_srgb,var(--kat)_14%,var(--color-bg-primary))]";
export const KAT_KANTE = "border-[var(--kat)]";
export const KAT_PUNKT = "bg-[var(--kat)]";

export function KategoriePunkt({ kategorie, className }: { kategorie: KategorieView | null; className?: string }) {
  return <span aria-hidden style={kategorieStil(kategorie)} className={cn("inline-block h-2 w-2 shrink-0 rounded-full", KAT_PUNKT, className)} />;
}

export function KategorieEtikett({ kategorie }: { kategorie: KategorieView | null }) {
  if (!kategorie) return null;
  return (
    <span
      style={kategorieStil(kategorie)}
      className={cn("text-caption text-text-primary inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 font-medium", KAT_GRUND)}
    >
      <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", KAT_PUNKT)} />
      {kategorie.name}
    </span>
  );
}

/** Zusagen in Kurzform: „12 Zusagen · 4 offen“, mit Hoechstzahl „10 / 10 · 2 warten“. */
export function zusagenKurz(v: Pick<VorkommenView, "zaehler" | "maxParticipants">): string | null {
  const z = v.zaehler;
  if (!z) return null;
  const ja = v.maxParticipants ? `${z.yes} / ${v.maxParticipants}` : `${z.yes} ${z.yes === 1 ? "Zusage" : "Zusagen"}`;
  const rest = [z.warteliste ? `${z.warteliste} warten` : null, z.pending ? `${z.pending} offen` : null].filter(Boolean);
  return [ja, ...rest].join(" · ");
}

export function VorkommenMarken({ v, kompakt = false }: { v: VorkommenView; kompakt?: boolean }) {
  const zusagen = zusagenKurz(v);
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {v.status === "abgesagt" && <TonBadge ton="danger">Abgesagt</TonBadge>}
      {v.status === "verschoben" && <TonBadge ton="warning">Verschoben</TonBadge>}
      {zusagen && v.status !== "abgesagt" && (
        <span className="text-caption text-text-secondary inline-flex items-center gap-1 tabular-nums">
          <UsersRound className="text-text-tertiary h-3.5 w-3.5" aria-hidden />
          {zusagen}
        </span>
      )}
      {v.freiePlaetze !== null && v.status !== "abgesagt" && (
        <TonBadge ton={v.freiePlaetze > 0 ? "warning" : "success"}>
          <span className="tabular-nums">{v.freiePlaetze > 0 ? `${v.freiePlaetze} Helfer gesucht` : "Helfer komplett"}</span>
        </TonBadge>
      )}
      {!kompakt && v.isSeries && (
        <span className="text-text-tertiary" title="Serientermin">
          <Repeat className="h-3.5 w-3.5" aria-label="Serientermin" />
        </span>
      )}
      {!kompakt && v.visibility === "PUBLIC" && (
        <span className="text-text-tertiary" title="Öffentlich, auch auf der Website">
          <Globe className="h-3.5 w-3.5" aria-label="Öffentlich" />
        </span>
      )}
      {!kompakt && (v.visibility === "BOARD" || v.visibility === "PRIVATE") && (
        <span className="text-text-tertiary" title="Nur für den Vorstand sichtbar">
          <Lock className="h-3.5 w-3.5" aria-label="Nur Vorstand" />
        </span>
      )}
    </span>
  );
}
